// ACTION ITEMS FOR USER:
// 1. Place logo files (e.g., volvo.svg, accenture.svg, dte.svg) in src/assets/logos/ and tell me the filenames.
// 2. Review/update related project names for accuracy.
// 3. Let me know if you want to add dates for all testimonials.

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Quote, Linkedin, ExternalLink, Briefcase, Lock, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Helper to build the header string with pipes, avoiding redundancy
function buildHeader({ role, company }) {
  // If role contains company, don't repeat
  if (role && company && role.toLowerCase().includes(company.toLowerCase())) {
    return role;
  }
  // If both present, join with pipe
  if (role && company) {
    return `${role} | ${company}`;
  }
  // If only one present
  return role || company || '';
}

const Testimonials = () => {
  const navigate = useNavigate();
  
  // TOGGLE: Testimonials Password Protection - Controls access to additional testimonials
  // Current State: false - Only Martin's testimonial is visible
  // How to Toggle: Enter password "nsmhumane" to unlock all testimonials
  // Category: User-Controlled
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Check if testimonials are already unlocked
  useEffect(() => {
    const unlocked = localStorage.getItem('testimonialsUnlocked') === 'true';
    if (unlocked) {
      navigate('/testimonials-unlocked');
    }
  }, [navigate]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === 'nsmhumane') {
      localStorage.setItem('testimonialsUnlocked', 'true');
      setPasswordError('');
      setShowPasswordModal(false);
      navigate('/testimonials-unlocked');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const testimonials = [
    {
      name: 'Martin Kristensson',
      role: 'VP, Autonomous Driving',
      company: 'Volvo Cars',
      logo: 'volvo-cars.svg',
      linkedin: 'https://www.linkedin.com/in/mkristensson/',
      date: '',
      short: 'Neil consistently delivered exceptional results managing strategic partnerships at Volvo. His analytical skills, industry insight, and proactive approach made him instrumental in driving new business opportunities.',
      full: `I am happy to write this recommendation for Neil, who has been an outstanding member of my team during 2022 and 2023. Neil worked as a business developer and partner manager, reporting directly to me.\n\nNeil is a highly skilled and dedicated professional, known for his social nature, analytical mindset, and strong work ethic. Throughout his tenure at Volvo, Neil successfully managed a portfolio of key partners, consistently delivering exceptional results. I was particularly impressed by his ability to identify and cultivate new business opportunities aligned with our strategic goals. This required in-depth insight from the car industry, as well as an understanding of business models from other domains. Neil is dedicated to the work and was instrumental in establishing the partner management team. He has an extensive network as well as a keen understanding of the tech domain. He consistently went above and beyond to meet deadlines and exceed expectations.\n\nI wholeheartedly recommend Neil for any future opportunities. He is a quick thinker, analytic, hardworking – and generally a great guy to have around! I have no doubt that he will make valuable contributions to any organization fortunate enough to have him on board. Please don't hesitate to reach out to me if you would like further information or have any questions about Neil's qualifications.`,
      relatedProjects: ['Product & Autonomous Strategy']
    },
    {
      name: 'Sunny Wong',
      role: '',
      company: 'Accenture',
      logo: 'accenture.svg',
      linkedin: 'https://www.linkedin.com/in/sunnykswong/',
      date: '',
      short: 'Neil expertly navigated challenging stakeholder discussions, translating complex technical details into clear business terms. His strategic insight, clarity, and confidence were critical in managing scope and aligning stakeholders.',
      full: `Neil demonstrated exceptional preparation, strategic communication, and firm but professional management of challenging stakeholder discussions during a critical project meeting, clearly translating technical information into actionable business language.\n\n"Neil was outstanding in today's meeting. He handled tough questions confidently and translated complex technical discussions into clear business terms. He professionally addressed suggested scope changes by firmly referring to prior agreements, demonstrating strong strategic insight and stakeholder management skills. It was clear Neil knew the subject matter thoroughly and communicated it effectively—a great example of leadership and clarity."`,
      relatedProjects: ['AI Strategy Transformation']
    },
    {
      name: 'Dondi S. Schneider',
      role: 'Senior Managing Director',
      company: 'Accenture',
      logo: 'accenture.svg',
      linkedin: 'https://www.linkedin.com/in/dondischneider/',
      date: '',
      short: 'Neil is an exceptional business operator whose strategic perspective and operational effectiveness greatly elevate our team. His contributions consistently drive impactful results.',
      full: `Neil is recognized as a highly capable business operator whose contributions significantly enhance team effectiveness and strategic execution.\n\n"This is a fantastic reflection of our teamwork and recognition of Neil's excellence. Neil consistently demonstrates strong strategic insight and operational effectiveness, making him an outstanding asset to our program. Proud to have him as part of the team."`,
      relatedProjects: ['AI Strategy Transformation', 'Digital Transformation ROI Model']
    },
    {
      name: 'Pankaj Narang',
      role: 'Head of Agile',
      company: 'DTE Energy',
      logo: 'dte.svg',
      linkedin: 'https://www.linkedin.com/in/pankaj2015/',
      date: '',
      short: 'Neil led the transition of our Reports and Controls initiative seamlessly from assessment to execution, emphasizing collaborative knowledge-sharing. His proactive leadership turned complex deliverables into clear, manageable outcomes.',
      full: `Neil played an instrumental role in seamlessly transitioning a significant Reports and Controls initiative from assessment to execution, emphasizing collaborative knowledge sharing and smooth operational handover.\n\n"I want to acknowledge Neil for his exceptional support in transitioning our Reports and Controls initiative smoothly from the assessment to execution phase. Neil ensured this transition was not merely a hand-off, but a true collaborative 'hand-shake,' effectively transforming complex deliverables into clear, actionable tasks. His proactive approach in facilitating cooperation and knowledge sharing was pivotal to our success. Outstanding work!"`,
      relatedProjects: ['Reports & Controls Initiative']
    },
    {
      name: 'Jennifer McAdams',
      role: 'Manager-Strategic Market Planning and Development',
      company: 'DTE Energy',
      logo: 'dte.svg',
      linkedin: 'https://www.linkedin.com/in/jennifer-mcadams-67889813/',
      date: '',
      short: 'Neil delivered exceptional results with a 99.7% conversion rate, far exceeding expectations. His dedication and precision saved our team hundreds of hours in manual corrections. I look forward to seeing his continued impact on future projects.',
      full: `Neil,\n\nYou were truly a breath of fresh air when I was feeling uneasy at Go-Live. I really appreciated your consistency and patience to deliver a 99.7% conversion rate. Way higher than previous consultants who could only estimate 50% conversion. With your hard work, you saved hundreds of hours of manual effort to correct system records. Best of luck on the Duke project — they're going to love working with you.`,
      relatedProjects: ['DTE Energy Go-Live Project']
    }
  ];

  // Helper to render logo or fallback
  function renderLogo(logo, fallback) {
    try {
      // eslint-disable-next-line import/no-dynamic-require, global-require
      return <img src={require(`../assets/logos/${logo}`)} alt={fallback} className="h-10 w-10 object-contain rounded bg-white border border-primary-100" />;
    } catch {
      return <span className="text-primary-800 font-bold text-sm">{fallback}</span>;
    }
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="section-title">Testimonials</h1>
            <p className="section-subtitle">
              What executives and leaders say about working with me
            </p>
            <p className="text-primary-700 text-lg leading-relaxed">
              These testimonials reflect the strategic impact and measurable results I've delivered 
              across Fortune 500 companies, high-growth startups, and innovative organizations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Show only Martin's testimonial */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="card group hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg">
                    {renderLogo(testimonials[0].logo, testimonials[0].company || testimonials[0].name)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary-900 flex items-center gap-2">
                      {testimonials[0].name}
                    </h3>
                    {buildHeader(testimonials[0]) && <p className="text-sm text-primary-600">{buildHeader(testimonials[0])}</p>}
                    {testimonials[0].date && <p className="text-xs text-primary-400 mt-1">{testimonials[0].date}</p>}
                  </div>
                </div>
                <Quote size={20} className="text-primary-300" />
              </div>
              <div className="mb-4 flex flex-row items-end justify-between">
                <div className="flex items-end">
                  <Quote size={20} className="text-primary-300 rotate-180" />
                </div>
                <div className="flex-1 ml-2">
                  <p className="text-primary-700 leading-relaxed">
                    {testimonials[0].short}
                  </p>
                  <a href={`#full-${testimonials[0].name.replace(/\s/g, '').toLowerCase()}`} className="text-primary-600 underline text-xs hover:text-primary-900">Read full testimonial</a>
                </div>
              </div>
            </motion.div>

            {/* Password Protected Cards */}
            {testimonials.slice(1).map((t, idx) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: (idx + 1) * 0.1 }}
                className="card group hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Blurred Background */}
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Lock size={32} className="text-primary-400 mx-auto mb-3" />
                    <p className="text-primary-600 font-medium mb-2">Additional Testimonial</p>
                    <p className="text-primary-500 text-sm mb-4">Enter password to view</p>
                    <button
                      onClick={() => setShowPasswordModal(true)}
                      className="btn-primary"
                    >
                      View All Testimonials
                    </button>
                  </div>
                </div>
                
                {/* Hidden Content (for SEO/accessibility) */}
                <div className="opacity-0 pointer-events-none">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 flex items-center justify-center rounded-lg">
                        {renderLogo(t.logo, t.company || t.name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary-900 flex items-center gap-2">
                          {t.name}
                        </h3>
                        {buildHeader(t) && <p className="text-sm text-primary-600">{buildHeader(t)}</p>}
                        {t.date && <p className="text-xs text-primary-400 mt-1">{t.date}</p>}
                      </div>
                    </div>
                    <Quote size={20} className="text-primary-300" />
                  </div>
                  <div className="mb-4 flex flex-row items-end justify-between">
                    <div className="flex items-end">
                      <Quote size={20} className="text-primary-300 rotate-180" />
                    </div>
                    <div className="flex-1 ml-2">
                      <p className="text-primary-700 leading-relaxed">
                        {t.short}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Password Modal */}
      {showPasswordModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowPasswordModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="card max-w-md w-full mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <Lock size={48} className="text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-primary-900 mb-2">View All Testimonials</h3>
              <p className="text-primary-600">Enter the password to access additional testimonials</p>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-primary-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter password"
                  required
                />
              </div>
              
              {passwordError && (
                <p className="text-red-600 text-sm">{passwordError}</p>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-3 border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  Unlock
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex justify-center mt-8 mb-4"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-primary-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>

      {/* Full Testimonials Section Header */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-4"
          >
            <h2 className="section-title">Full Testimonials</h2>
            <p className="section-subtitle">
              Detailed feedback and recommendations from executives and leaders
            </p>
          </motion.div>
        </div>
      </section>

      {/* Full Testimonials Section */}
      <section className="section bg-white" id={`full-${testimonials[0].name.replace(/\s/g, '').toLowerCase()}`}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl p-8 border border-primary-100 shadow-lg">
            {/* Header with LinkedIn */}
            <div className="flex items-start justify-between mb-6 pb-4 border-b border-primary-100">
              <div>
                <h3 className="text-2xl font-bold text-primary-900 mb-2">{testimonials[0].name}</h3>
                <div className="flex items-center gap-4 text-primary-700">
                  {buildHeader(testimonials[0]) && <span className="font-medium">{buildHeader(testimonials[0])}</span>}
                  {testimonials[0].date && <span className="text-sm text-primary-500">{testimonials[0].date}</span>}
                </div>
              </div>
              {testimonials[0].linkedin && (
                <a 
                  href={testimonials[0].linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 bg-primary-50 hover:bg-primary-100 text-primary-700 px-4 py-2 rounded-lg transition-colors"
                >
                  <Linkedin size={20} />
                  <span className="font-medium">LinkedIn</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
            
            {/* Testimonial Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-primary-800 leading-relaxed whitespace-pre-line mb-6">{testimonials[0].full}</p>
            </div>

            {/* Related Projects Section */}
            {testimonials[0].relatedProjects && testimonials[0].relatedProjects.length > 0 && (
              <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-100">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase size={20} className="text-primary-700" />
                  <h4 className="font-semibold text-primary-900">Related Projects</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {testimonials[0].relatedProjects.map((project, index) => (
                    <a
                      key={index}
                      href="/projects"
                      className="inline-flex items-center gap-1 bg-primary-100 hover:bg-primary-200 text-primary-800 px-3 py-1 rounded-full text-sm font-medium transition-colors"
                    >
                      {project}
                      <ExternalLink size={14} />
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-primary-100">
              <a href="#top" className="text-primary-600 hover:text-primary-900 font-medium flex items-center gap-2">
                ← Back to testimonials
              </a>
              <div className="text-sm text-primary-500">
                Full testimonial from {testimonials[0].name}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="section-title">Ready to Join These Success Stories?</h2>
            <p className="section-subtitle">
              Let's discuss how I can help drive strategic transformation and measurable results for your organization
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="btn-primary"
              >
                Start a Conversation
              </a>
              <a
                href="/projects"
                className="btn-secondary"
              >
                View Case Studies
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials; 